import { myLocalStorage } from "../../../StorageHelper";

export type DashboardEntityCloudProvider = "aws" | "gcp" | "azure";
export type DashboardEntityKind = "user" | "nhi" | "secret";
export type DashboardGraphConnectionType = "users" | "sa" | "resources";

export interface DashboardAnalyticsPayload {
  cloudProvider: DashboardEntityCloudProvider;
  tenantName: string;
  entityId: string;
  entityType: "user" | "nhi";
  projectId: string;
  roles: any[];
  syncedAt: any;
  email: string;
  entityName: string;
  cloudNHIType: string;
  details: any;
  extrasdetails: any;
}

export interface DashboardGraphQuickLinkPayload {
  cloudProvider: DashboardEntityCloudProvider;
  cloudType: "AWS" | "GCP" | "AZURE";
  tenantName: string;
  cloudId: string;
  activeConnectionType: DashboardGraphConnectionType;
  activeConnectionSubFilter?: string | null;
  targetNodeType: "user" | "service_account" | "resource";
  preferredNodeIds: string[];
  preferredGraphIds: string[];
  candidateValues: string[];
  targetLabel: string;
  preferredSearchQuery: string;
}

const ANALYTICS_LINK_PREFIX = "cloud-dashboard-analytics-link";
const GRAPH_LINK_PREFIX = "cloud-dashboard-graph-link";

const parseStoredValue = (value: any) => {
  if (!value) return null;

  if (typeof value === "string") {
    try {
      return JSON.parse(value);
    } catch {
      return null;
    }
  }

  return typeof value === "object" ? value : null;
};

const readStructuredLocalStorageValue = (key: string) => {
  const rawValue = localStorage.getItem(key);

  if (!rawValue) {
    return "";
  }

  const parsedValue = parseStoredValue(rawValue);

  if (
    parsedValue &&
    typeof parsedValue === "object" &&
    "value" in parsedValue
  ) {
    return String(parsedValue.value || "");
  }

  return String(rawValue);
};

const normalizeCloudProvider = (
  cloudProvider: string,
): DashboardEntityCloudProvider => {
  const normalized = String(cloudProvider || "").toLowerCase();

  if (normalized === "aws") return "aws";
  if (normalized === "azure") return "azure";

  return "gcp";
};

const getCloudContext = (cloudProvider: DashboardEntityCloudProvider) => {
  const latestTenant = parseStoredValue(myLocalStorage.getItem("latestTenant"));
  const latestSubList = parseStoredValue(myLocalStorage.getItem("latestSubList"));
  const gcpProjectId = myLocalStorage.getItem("gcpSelectedProjectId") || "";
  const awsAccountId =
    myLocalStorage.getItem("selectedAwsAccount") ||
    readStructuredLocalStorageValue("awsSelectedAccountId") ||
    "";
  const azureSubscriptionId =
    latestSubList?.azureSubscriptionId ||
    readStructuredLocalStorageValue("azureSelectedSubscriptionId") ||
    "";
  const azureSubscriptionName =
    latestSubList?.subscriptionName || latestSubList?.name || azureSubscriptionId;
  const tenantName = latestTenant?.tenantName || latestTenant?.displayName || "";

  if (cloudProvider === "aws") {
    return {
      projectId: awsAccountId,
      entityName: awsAccountId,
      tenantName,
      cloudType: "AWS" as const,
    };
  }

  if (cloudProvider === "azure") {
    return {
      projectId: azureSubscriptionId,
      entityName: azureSubscriptionName,
      tenantName,
      cloudType: "AZURE" as const,
    };
  }

  return {
    projectId: gcpProjectId,
    entityName: gcpProjectId,
    tenantName,
    cloudType: "GCP" as const,
  };
};

const setStructuredLocalStorageValue = (key: string, value: any) => {
  localStorage.setItem(key, JSON.stringify({ value }));
};

const primeDashboardCloudContext = ({
  cloudProvider,
  tenantName,
  projectId,
  resourceName,
}: {
  cloudProvider: DashboardEntityCloudProvider;
  tenantName?: string;
  projectId?: string;
  resourceName?: string;
}) => {
  if (tenantName) {
    myLocalStorage.setItem("latestTenant", {
      tenantName,
      displayName: tenantName,
    });
  }

  if (cloudProvider === "gcp") {
    myLocalStorage.setItem("gcpSelectedProjectId", projectId || "");
    return;
  }

  if (cloudProvider === "aws") {
    myLocalStorage.setItem("selectedAwsAccount", projectId || "");
    setStructuredLocalStorageValue("awsSelectedAccountId", projectId || "");
    return;
  }

  if (projectId || resourceName) {
    myLocalStorage.setItem(
      "latestSubList",
      JSON.stringify({
        azureSubscriptionId: projectId || "",
        subscriptionName: resourceName || projectId || "",
        name: resourceName || projectId || "",
      }),
    );
  }

  setStructuredLocalStorageValue(
    "azureSelectedSubscriptionId",
    projectId || "",
  );
};

const getRoleArn = (entity: any) => {
  const directRoleArn = entity?.roleArn || entity?.roleARN || entity?.arn;
  if (directRoleArn) return directRoleArn;

  const extractedRoles = extractEntityRoles(entity);

  if (extractedRoles.length > 0) {
    const matchingRole = extractedRoles.find(
      (role: any) => role?.roleArn || role?.roleARN || role?.arn,
    );

    if (matchingRole) {
      return matchingRole.roleArn || matchingRole.roleARN || matchingRole.arn;
    }
  }

  return "";
};

const isRoleLikeObject = (value: any) =>
  Boolean(
    value &&
      typeof value === "object" &&
      (
        value?.roleName ||
        value?.roleArn ||
        value?.roleARN ||
        value?.arn ||
        value?.title ||
        value?.role ||
        value?.roleId ||
        value?.roleDefinitionName ||
        value?.name ||
        value?.displayName
      ),
  );

const flattenRoleSource = (source: any): any[] => {
  if (!source) return [];

  if (Array.isArray(source)) {
    return source.flatMap((item) => flattenRoleSource(item));
  }

  if (typeof source === "string") {
    return source
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
  }

  if (isRoleLikeObject(source)) {
    return [source];
  }

  return [];
};

const normalizeRoleEntry = (role: any) => {
  if (!role) return null;

  if (typeof role === "string") {
    const roleName = role.trim();
    return roleName ? { roleName } : null;
  }

  if (typeof role !== "object") {
    return null;
  }

  const roleName =
    role?.roleName ||
    role?.title ||
    role?.role ||
    role?.roleDefinitionName ||
    role?.displayName ||
    role?.name ||
    role?.roleId ||
    role?.roleArn ||
    role?.roleARN ||
    role?.arn;
  const roleArn = role?.roleArn || role?.roleARN || role?.arn || "";

  if (!roleName && !roleArn) {
    return null;
  }

  return {
    ...role,
    roleName: roleName || roleArn,
    ...(roleArn
      ? {
          roleArn,
          roleARN: role?.roleARN || roleArn,
          arn: role?.arn || roleArn,
        }
      : {}),
  };
};

const dedupeRoles = (roles: any[]) => {
  const seen = new Set<string>();

  return roles.filter((role) => {
    const key = `${String(role?.roleName || "").trim().toLowerCase()}::${String(
      role?.roleArn || role?.roleARN || role?.arn || "",
    )
      .trim()
      .toLowerCase()}`;

    if (seen.has(key)) {
      return false;
    }

    seen.add(key);
    return true;
  });
};

const extractEntityRoles = (entity: any) =>
  dedupeRoles(
    [
      entity?.roles,
      entity?.serviceroles,
      entity?.serviceRoles,
      entity?.roleDefinitions,
      entity?.roleNames,
      entity?.roleDetails,
      entity?.iamRoleDetails,
      entity?.serviceDetails,
      entity?.serviceDetails?.roles,
      entity?.serviceDetails?.serviceroles,
      entity?.serviceDetails?.serviceRoles,
      entity?.serviceDetails?.roleDefinitions,
      entity?.serviceDetails?.roleDetails,
      entity?.serviceDetails?.iamRoleDetails,
      entity?.details?.roles,
      entity?.details?.serviceroles,
      entity?.details?.serviceRoles,
      entity?.details?.roleDefinitions,
      entity?.details?.roleNames,
      entity?.details?.roleDetails,
      entity?.details?.iamRoleDetails,
      entity?.details?.serviceDetails,
      entity?.extrasdetails?.roles,
      entity?.extrasdetails?.serviceroles,
      entity?.extrasdetails?.serviceRoles,
      entity?.extrasdetails?.roleDefinitions,
      entity?.extrasdetails?.roleNames,
      entity?.extrasdetails?.roleDetails,
      entity?.extrasdetails?.iamRoleDetails,
      entity?.extrasdetails?.serviceDetails,
      entity?.item,
      entity?.item?.roles,
      entity?.item?.serviceroles,
      entity?.item?.serviceRoles,
      entity?.item?.roleDefinitions,
      entity?.item?.roleNames,
      entity?.item?.roleDetails,
      entity?.item?.iamRoleDetails,
      entity?.item?.serviceDetails,
      entity?.item?.serviceDetails?.roles,
      entity?.item?.serviceDetails?.serviceroles,
      entity?.item?.serviceDetails?.serviceRoles,
      entity?.item?.serviceDetails?.roleDefinitions,
      entity?.item?.serviceDetails?.roleDetails,
      entity?.item?.serviceDetails?.iamRoleDetails,
      entity?.sa?.roles,
      entity?.sa?.serviceroles,
      entity?.sa?.serviceRoles,
      entity?.sa?.roleDetails,
      entity?.sa?.iamRoleDetails,
      entity?.sa?.serviceDetails,
    ]
      .flatMap((source) => flattenRoleSource(source))
      .map((role) => normalizeRoleEntry(role))
      .filter(Boolean),
  );

const getPrincipalId = (entity: any) =>
  entity?.principalId ||
  entity?.azureId ||
  entity?.azureUserId ||
  entity?.uniqueId ||
  "";

const getServiceAccountIdentifier = (entity: any) =>
  entity?.serviceAccountEmail ||
  entity?.email ||
  entity?.principalName ||
  entity?.userPrincipalName ||
  entity?.name ||
  "";

const getSecretIdentifier = (entity: any) =>
  entity?.secretName ||
  entity?.secretArn ||
  entity?.arn ||
  entity?.name ||
  entity?.displayName ||
  "";

const toUniqueStrings = (values: any[]) =>
  Array.from(
    new Set(
      values
        .flatMap((value) => {
          if (Array.isArray(value)) return value;
          return [value];
        })
        .map((value) => String(value || "").trim())
        .filter(Boolean),
    ),
  );

const expandIdentifierVariants = (value: string) => {
  const normalizedValue = String(value || "").trim();

  if (!normalizedValue) {
    return [];
  }

  const slashTail = normalizedValue.includes("/")
    ? normalizedValue.split("/").filter(Boolean).pop()
    : "";
  const colonTail = normalizedValue.includes(":")
    ? normalizedValue.split(":").filter(Boolean).pop()
    : "";

  return toUniqueStrings([normalizedValue, slashTail, colonTail]);
};

const collectCandidateValues = (...values: any[]) =>
  toUniqueStrings(values.flatMap((value) => expandIdentifierVariants(value)));

const getNhiAnalyticsRoute = (cloudProvider: DashboardEntityCloudProvider) => {
  if (cloudProvider === "aws") {
    return "/aws/nhi/service-account-details";
  }

  if (cloudProvider === "azure") {
    return "/azure-directory/nhi/service-account-details";
  }

  return "/gcp/nhi/service-account-details";
};

const inferCloudNhiType = (entity: any) => {
  const explicitType = String(
    entity?.cloudNHIType || entity?.nhiType || entity?.type || "",
  ).toUpperCase();

  if (
    [
      "SECRET",
      "TOKEN",
      "K8SERVICE",
      "K8TOKEN",
      "K8SECRET",
      "OIDC",
    ].includes(explicitType)
  ) {
    return explicitType;
  }

  return "";
};

const inferServiceAccountSubFilter = (entity: any) => {
  const isUserManaged =
    entity?.isUserManaged ??
    entity?.details?.isUserManaged ??
    entity?.extrasdetails?.isUserManaged;

  if (isUserManaged === true) {
    return "user-managed";
  }

  if (isUserManaged === false) {
    return "cloud-managed";
  }

  return null;
};

const resolveEntitySyncedAt = (entity: any) =>
  entity?.syncedAt ||
  entity?.serviceDetails?.syncedAt ||
  entity?.details?.syncedAt ||
  entity?.extrasdetails?.syncedAt ||
  entity?.item?.syncedAt ||
  entity?.sa?.syncedAt ||
  entity?.createDate ||
  entity?.createdAt ||
  entity?.updatedAt ||
  entity?.serviceDetails?.createDate ||
  entity?.serviceDetails?.createdAt ||
  entity?.details?.createDate ||
  entity?.details?.createdAt ||
  entity?.item?.createDate ||
  entity?.item?.createdAt ||
  entity?.sa?.createDate ||
  entity?.sa?.createdAt ||
  entity?.lastActivity ||
  entity?.validityPeriod ||
  "";

const buildSecretPayload = (
  cloudProvider: DashboardEntityCloudProvider,
  entity: any,
): DashboardAnalyticsPayload | null => {
  const { projectId, entityName, tenantName } = getCloudContext(cloudProvider);
  const secretName =
    entity?.secretName ||
    entity?.secretArn ||
    entity?.arn ||
    entity?.name ||
    entity?.displayName;

  if (!secretName) {
    return null;
  }

  return {
    cloudProvider,
    tenantName,
    entityId: secretName,
    entityType: "nhi",
    projectId,
    roles: extractEntityRoles(entity),
    syncedAt:
      resolveEntitySyncedAt(entity) ||
      entity?.lastRotated ||
      "",
    email: entity?.displayName || entity?.name || secretName,
    entityName,
    cloudNHIType: "SECRET",
    details: {
      ...(entity?.details || {}),
      cloudType: cloudProvider.toUpperCase(),
      cloudNHIType: "SECRET",
      secretName,
      cloudSecret: entity?.cloudSecret || entity,
    },
    extrasdetails: entity?.cloudSecret || entity,
  };
};

const buildNhiPayload = (
  cloudProvider: DashboardEntityCloudProvider,
  entity: any,
): DashboardAnalyticsPayload | null => {
  const cloudNHIType = inferCloudNhiType(entity);

  if (cloudNHIType === "SECRET") {
    return buildSecretPayload(cloudProvider, entity);
  }

  const { projectId, entityName, tenantName } = getCloudContext(cloudProvider);
  const roleArn = getRoleArn(entity);
  const principalId =
    entity?.principalId ||
    entity?.azureId ||
    entity?.azureUserId ||
    entity?.uniqueId ||
    "";
  const serviceAccountEmail =
    entity?.serviceAccountEmail ||
    entity?.email ||
    entity?.principalName ||
    entity?.userPrincipalName ||
    entity?.name ||
    "";
  const entityId = roleArn || principalId || serviceAccountEmail;

  if (!entityId) {
    return null;
  }

  const roles =
    extractEntityRoles(entity).length > 0
      ? extractEntityRoles(entity)
      : roleArn
        ? [
            {
              roleName: entity?.name || roleArn,
              roleArn,
              roleARN: roleArn,
            },
          ]
        : [];

  return {
    cloudProvider,
    tenantName,
    entityId,
    entityType: "nhi",
    projectId,
    roles,
    syncedAt: resolveEntitySyncedAt(entity),
    email: serviceAccountEmail || entity?.name || entityId,
    entityName,
    cloudNHIType,
    details: {
      ...(entity?.details || {}),
      cloudType: cloudProvider.toUpperCase(),
      cloudNHIType,
      serviceAccountEmail,
      principalId,
      roles,
      roleDetails:
        entity?.roleDetails ||
        entity?.iamRoleDetails ||
        entity?.serviceDetails ||
        entity?.details?.roleDetails ||
        entity?.extrasdetails?.roleDetails,
      secretName: entity?.secretName || entity?.name,
      cloudTokenDetails: entity?.cloudTokenDetails,
      serviceDetails: entity?.serviceDetails,
      tokenDetails: entity?.tokenDetails,
      secretDetails: entity?.secretDetails,
      oidcARN: entity?.oidcARN,
      oidc: entity?.oidc,
      from:
        entity?.from ||
        (cloudProvider === "aws" && roleArn ? "IAMRoleNHI" : "CloudDashboard"),
    },
    extrasdetails: entity?.extrasdetails || entity,
  };
};

const buildUserPayload = (
  cloudProvider: DashboardEntityCloudProvider,
  entity: any,
): DashboardAnalyticsPayload | null => {
  const { projectId, entityName, tenantName } = getCloudContext(cloudProvider);
  const normalizedRoles = extractEntityRoles(entity);

  const entityId =
    cloudProvider === "aws"
      ? entity?.arn || entity?.roleArn || entity?.uniqueId || entity?.name
      : cloudProvider === "azure"
        ? entity?.azureId || entity?.azureUserId || entity?.principalId || entity?.uniqueId
        : entity?.email || entity?.userPrincipalName || entity?.name;

  if (!entityId) {
    return null;
  }

  return {
    cloudProvider,
    tenantName,
    entityId,
    entityType: "user",
    projectId,
    roles: normalizedRoles,
    syncedAt: resolveEntitySyncedAt(entity),
    email:
      entity?.email ||
      entity?.userPrincipalName ||
      entity?.displayName ||
      entity?.name ||
      entityId,
    entityName,
    cloudNHIType: "",
    details: {},
    extrasdetails: {},
  };
};

const buildSecretGraphPayload = (
  cloudProvider: DashboardEntityCloudProvider,
  entity: any,
): DashboardGraphQuickLinkPayload | null => {
  const { projectId, tenantName, cloudType } = getCloudContext(cloudProvider);
  const secretName = getSecretIdentifier(entity);

  if (!tenantName || !projectId || !secretName) {
    return null;
  }

  const exactIdentifiers = toUniqueStrings([
    secretName,
    entity?.displayName,
    entity?.name,
    entity?.arn,
    entity?.secretArn,
  ]);

  return {
    cloudProvider,
    cloudType,
    tenantName,
    cloudId: projectId,
    activeConnectionType: "resources",
    activeConnectionSubFilter: null,
    targetNodeType: "resource",
    preferredNodeIds: exactIdentifiers.map(
      (identifier) => `resource-center-${identifier}`,
    ),
    preferredGraphIds: exactIdentifiers.map(
      (identifier) => `resource-center-${identifier}`,
    ),
    candidateValues: collectCandidateValues(...exactIdentifiers),
    targetLabel: entity?.displayName || entity?.name || secretName,
    preferredSearchQuery:
      entity?.displayName || entity?.name || secretName || exactIdentifiers[0],
  };
};

const buildNhiGraphPayload = (
  cloudProvider: DashboardEntityCloudProvider,
  entity: any,
): DashboardGraphQuickLinkPayload | null => {
  const cloudNHIType = inferCloudNhiType(entity);

  if (cloudNHIType === "SECRET") {
    return buildSecretGraphPayload(cloudProvider, entity);
  }

  const { projectId, tenantName, cloudType } = getCloudContext(cloudProvider);
  const roleArn = getRoleArn(entity);
  const principalId = getPrincipalId(entity);
  const serviceAccountIdentifier = getServiceAccountIdentifier(entity);
  const exactIdentifiers = toUniqueStrings([
    serviceAccountIdentifier,
    roleArn,
    principalId,
    entity?.name,
    entity?.displayName,
    entity?.entityId,
  ]);

  if (!tenantName || !projectId || exactIdentifiers.length === 0) {
    return null;
  }

  return {
    cloudProvider,
    cloudType,
    tenantName,
    cloudId: projectId,
    activeConnectionType: "sa",
    activeConnectionSubFilter:
      cloudProvider === "gcp" ? inferServiceAccountSubFilter(entity) : null,
    targetNodeType: "service_account",
    preferredNodeIds: exactIdentifiers.map((identifier) => `sa-${identifier}`),
    preferredGraphIds: exactIdentifiers.map((identifier) => `sa-${identifier}`),
    candidateValues: collectCandidateValues(...exactIdentifiers),
    targetLabel:
      entity?.displayName || entity?.name || serviceAccountIdentifier || roleArn,
    preferredSearchQuery:
      serviceAccountIdentifier ||
      entity?.displayName ||
      entity?.name ||
      roleArn ||
      principalId ||
      exactIdentifiers[0],
  };
};

const buildUserGraphPayload = (
  cloudProvider: DashboardEntityCloudProvider,
  entity: any,
): DashboardGraphQuickLinkPayload | null => {
  const { projectId, tenantName, cloudType } = getCloudContext(cloudProvider);
  const exactIdentifiers = toUniqueStrings([
    entity?.email,
    entity?.userPrincipalName,
    entity?.mail,
    entity?.arn,
    entity?.roleArn,
    entity?.azureId,
    entity?.azureUserId,
    entity?.principalId,
    entity?.uniqueId,
    entity?.name,
    entity?.displayName,
  ]);

  if (!tenantName || !projectId || exactIdentifiers.length === 0) {
    return null;
  }

  return {
    cloudProvider,
    cloudType,
    tenantName,
    cloudId: projectId,
    activeConnectionType: "users",
    activeConnectionSubFilter: null,
    targetNodeType: "user",
    preferredNodeIds: exactIdentifiers.map((identifier) => `user-${identifier}`),
    preferredGraphIds: exactIdentifiers.map((identifier) => `user-${identifier}`),
    candidateValues: collectCandidateValues(...exactIdentifiers),
    targetLabel:
      entity?.displayName ||
      entity?.name ||
      entity?.email ||
      entity?.userPrincipalName,
    preferredSearchQuery:
      entity?.email ||
      entity?.userPrincipalName ||
      entity?.displayName ||
      entity?.name ||
      exactIdentifiers[0],
  };
};

export const buildDashboardAnalyticsPayload = ({
  cloudProvider,
  entityKind,
  entity,
}: {
  cloudProvider: string;
  entityKind: DashboardEntityKind;
  entity: any;
}): DashboardAnalyticsPayload | null => {
  const normalizedCloudProvider = normalizeCloudProvider(cloudProvider);

  if (entityKind === "secret") {
    return buildSecretPayload(normalizedCloudProvider, entity);
  }

  if (entityKind === "user") {
    return buildUserPayload(normalizedCloudProvider, entity);
  }

  return buildNhiPayload(normalizedCloudProvider, entity);
};

export const buildDashboardGraphPayload = ({
  cloudProvider,
  entityKind,
  entity,
}: {
  cloudProvider: string;
  entityKind: DashboardEntityKind;
  entity: any;
}): DashboardGraphQuickLinkPayload | null => {
  const normalizedCloudProvider = normalizeCloudProvider(cloudProvider);

  if (entityKind === "secret") {
    return buildSecretGraphPayload(normalizedCloudProvider, entity);
  }

  if (entityKind === "user") {
    return buildUserGraphPayload(normalizedCloudProvider, entity);
  }

  return buildNhiGraphPayload(normalizedCloudProvider, entity);
};

export const createDashboardAnalyticsQuickLink = (
  payload: DashboardAnalyticsPayload,
) => {
  const linkId =
    typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

  myLocalStorage.setItem(`${ANALYTICS_LINK_PREFIX}:${linkId}`, payload, 30);

  return `/dashboard/cloud-entity-analytics?linkId=${encodeURIComponent(linkId)}`;
};

export const createDashboardGraphQuickLink = (
  payload: DashboardGraphQuickLinkPayload,
) => {
  const linkId =
    typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

  myLocalStorage.setItem(`${GRAPH_LINK_PREFIX}:${linkId}`, payload, 30);

  return `/cloud-analytics-view?graphLinkId=${encodeURIComponent(linkId)}`;
};

const createNhiRouteState = (payload: DashboardAnalyticsPayload) => {
  const latestTenant = parseStoredValue(myLocalStorage.getItem("latestTenant"));
  const latestSubList = parseStoredValue(myLocalStorage.getItem("latestSubList"));
  const accountId = myLocalStorage.getItem("selectedAwsAccount") || "";
  const rawItem =
    payload.extrasdetails?.item ||
    payload.extrasdetails ||
    payload.details?.item ||
    payload.details?.serviceDetails ||
    payload.details;
  const routeRoles =
    Array.isArray(payload.roles) && payload.roles.length > 0
      ? payload.roles
      : extractEntityRoles({
          roles: payload.details?.roles,
          roleDetails: payload.details?.roleDetails,
          iamRoleDetails: payload.details?.iamRoleDetails,
          serviceDetails: payload.details?.serviceDetails,
          extrasdetails: payload.extrasdetails,
          item: rawItem,
        });

  return {
    tenantName: latestTenant?.tenantName || "",
    cloudType: payload.cloudProvider.toUpperCase(),
    cloudNHIType: payload.cloudNHIType || "",
    serviceAccountEmail:
      payload.details?.serviceAccountEmail ||
      payload.email ||
      payload.entityId,
    roles: routeRoles,
    syncedAt: resolveEntitySyncedAt(rawItem) || payload.syncedAt,
    principalId: payload.details?.principalId || "",
    projectId: payload.projectId,
    latestSubList,
    accountId,
    item: rawItem,
    sa: rawItem,
    from: payload.details?.from || "CloudDashboard",
    secretName: payload.details?.secretName,
    nameSpace: payload.details?.nameSpace,
    oidcARN: payload.details?.oidcARN,
    oidc: payload.details?.oidc,
    cloudTokenDetails:
      payload.details?.cloudTokenDetails || payload.extrasdetails?.cloudTokenDetails,
    tokenDetails:
      payload.details?.tokenDetails || payload.extrasdetails?.tokenDetails,
    serviceDetails:
      payload.details?.serviceDetails || payload.extrasdetails?.serviceDetails,
    secretDetails:
      payload.details?.secretDetails || payload.extrasdetails?.secretDetails,
    cloudSecret:
      payload.details?.cloudSecret ||
      payload.extrasdetails?.cloudSecret ||
      (payload.cloudNHIType === "SECRET" ? payload.extrasdetails : undefined),
    roleDetails:
      payload.details?.roleDetails ||
      payload.details?.iamRoleDetails ||
      payload.details?.serviceDetails ||
      rawItem?.roleDetails ||
      rawItem?.iamRoleDetails,
  };
};

export const openDashboardAnalyticsQuickLink = (
  payload: DashboardAnalyticsPayload,
) => {
  primeDashboardCloudContext({
    cloudProvider: payload.cloudProvider,
    tenantName: payload.tenantName,
    projectId: payload.projectId,
    resourceName: payload.entityName,
  });

  if (payload.entityType === "nhi") {
    const routeState = createNhiRouteState(payload);
    const url = getNhiAnalyticsRoute(payload.cloudProvider);

    myLocalStorage.setItem("nhi-details", JSON.stringify(routeState));
    window.open(url, "_blank");
    return;
  }

  const url = createDashboardAnalyticsQuickLink(payload);
  window.open(url, "_blank");
};

export const openDashboardGraphQuickLink = (
  payload: DashboardGraphQuickLinkPayload,
) => {
  primeDashboardCloudContext({
    cloudProvider: payload.cloudProvider,
    tenantName: payload.tenantName,
    projectId: payload.cloudId,
  });

  const url = createDashboardGraphQuickLink(payload);
  window.open(url, "_blank");
};

export const readDashboardAnalyticsQuickLink = (linkId: string) => {
  if (!linkId) return null;

  return myLocalStorage.getItem(`${ANALYTICS_LINK_PREFIX}:${linkId}`) as
    | DashboardAnalyticsPayload
    | null;
};

export const readDashboardGraphQuickLink = (linkId: string) => {
  if (!linkId) return null;

  return myLocalStorage.getItem(`${GRAPH_LINK_PREFIX}:${linkId}`) as
    | DashboardGraphQuickLinkPayload
    | null;
};
